/**
 * Map Type Toggle Plugin for Search Results Map
 *
 * Wires up the Road/Satellite toggle buttons rendered in the mobile more-menu
 * panel (MoreMenuPanel.cshtml) to the Google Maps facade.
 *
 * Responsibilities:
 * - Attach click listeners to the road/satellite buttons
 * - Call mapFacade.setMapType() on click
 * - Keep button active-state CSS in sync with the current map type
 * - Clean up listeners on stop
 *
 * Reason to change: how the road/satellite toggle interacts with the map
 * facade or how the active-state CSS is managed.
 *
 * @module searchResultsMap/plugins/mapTypeToggle.plugin
 */

import MapTypes from 'enums/mapTypes';

const ACTIVE_CLASS = 'nhs-c-map__map-type-btn--active';

export default function createMapTypeTogglePlugin({ roadButton = null, satelliteButton = null } = {}) {
  const state = {
    mapFacade: null,
    started: false,
  };

  const mapTypeButtons = [
    { button: roadButton, type: MapTypes.Roadmap },
    { button: satelliteButton, type: MapTypes.Satellite },
  ];

  function setActiveButton(activeType) {
    mapTypeButtons.forEach(({ button, type }) => {
      if (!button) {
        return;
      }
      const isActive = activeType === type;
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  function onRoadClick() {
    state.mapFacade.setMapType(MapTypes.Roadmap.toString());
    setActiveButton(MapTypes.Roadmap);
  }

  function onSatelliteClick() {
    state.mapFacade.setMapType(MapTypes.Satellite.toString());
    setActiveButton(MapTypes.Satellite);
  }

  return {
    name: 'map-type-toggle',

    async init(context) {
      context.state.mapTypeToggle = { started: false };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;

      if (!state.mapFacade || !roadButton || !satelliteButton) {
        return;
      }

      roadButton.addEventListener('click', onRoadClick);
      satelliteButton.addEventListener('click', onSatelliteClick);

      state.started = true;
      context.state.mapTypeToggle = { started: true };
    },

    async stop(context) {
      if (roadButton) {
        roadButton.removeEventListener('click', onRoadClick);
      }
      if (satelliteButton) {
        satelliteButton.removeEventListener('click', onSatelliteClick);
      }
      state.mapFacade = null;
      state.started = false;
      context.state.mapTypeToggle = { started: false };
    },
  };
}
